(function($) {
	'use strict';

	/**
	 * Calculate average scores by control titles
	 * 
	 * This function calculates average maturity scores for control titles within different standards.
	 * It processes project items and distributes their maturity scores to the appropriate control titles
	 * based on the CRF mappings defined in groupedControlTitles.
	 * 
	 * @param {Array} projectItems - Array of project items with maturity scores (should be processedItems)
	 * @param {Object} groupedControlTitles - Object containing control titles organized by standard ID
	 * @returns {Object} Object containing average scores by standard ID and control title
	 */
	function calculateAverageScoresByControlTitles(projectItems, groupedControlTitles) {
		// console.log('[calculateAverageScoresByControlTitles] Starting calculation (FCA-RM-2024 only):', {
		// 	projectItemsCount: projectItems.length,
		// 	groupedControlTitlesFCA: groupedControlTitles['FCA-RM-2024'] ? Object.keys(groupedControlTitles['FCA-RM-2024']).length : 0
		// });

		const controlScores = {};
		const controlCounts = {};

		// Initialize control scores and count the unique safeguardIds for each controltitle, but only for FCA-RM-2024
		Object.keys(groupedControlTitles).forEach(standardid => {
			if (standardid !== 'FCA-RM-2024') return;
			// console.log(`[calculateAverageScoresByControlTitles] Processing standard: ${standardid}`);
			
			controlScores[standardid] = {}; // Initialize scores for each standardid
			controlCounts[standardid] = {}; // Initialize counts for each standardid

			Object.keys(groupedControlTitles[standardid]).forEach(controltitle => {
				const crfmappings = groupedControlTitles[standardid][controltitle].crfmappings;
				// console.log(`[calculateAverageScoresByControlTitles] [${standardid}] Control title: ${controltitle} with ${crfmappings.length} mappings`);

				// Initialize the controltitle under each standardid
				controlScores[standardid][controltitle] = 0;
				controlCounts[standardid][controltitle] = new Set(); // Use a Set to track unique safeguard IDs

				// Add all crfmappings to the Set for the controltitle
				crfmappings.forEach(crfmapping => {
					controlCounts[standardid][controltitle].add(crfmapping);
				});
			});
		});

	// Convert Sets to their sizes (count of unique crfmappings), only FCA-RM-2024
	Object.keys(controlCounts).forEach(standardid => {
		if (standardid !== 'FCA-RM-2024') return;
		Object.keys(controlCounts[standardid]).forEach(controltitle => {
			const originalSize = controlCounts[standardid][controltitle].size;
			const mappingsArray = Array.from(controlCounts[standardid][controltitle]);
			controlCounts[standardid][controltitle] = originalSize;
			// console.log(`[calculateAverageScoresByControlTitles] [${standardid}] ${controltitle}: Found ${originalSize} unique mappings:`, mappingsArray);
		});
	});

	// Iterate through each project item and distribute scores, but only for FCA-RM-2024
	const projectItemsWithScores = projectItems.filter(item => item.maturityscore > 0);
	// console.log(`[calculateAverageScoresByControlTitles] Found ${projectItemsWithScores.length} project items with maturity scores > 0`);
	
	// Debug: Show all SafeguardIDs from project items with scores
	const allSafeguardIdsWithScores = projectItemsWithScores.map(item => item.safeguardid.S);
	// console.log(`[DEBUG] All SafeguardIDs with scores (${allSafeguardIdsWithScores.length} total):`, allSafeguardIdsWithScores);
	
	projectItems.forEach((item, index) => {
		const safeguardId = item.safeguardid.S;
		const maturityScore = item.maturityscore;
		
		// Only process FCA-RM-2024
		Object.keys(groupedControlTitles).forEach(standardid => {
			if (standardid !== 'FCA-RM-2024') return;
			Object.keys(groupedControlTitles[standardid]).forEach(controltitle => {
				if (groupedControlTitles[standardid][controltitle].crfmappings.includes(safeguardId)) {
					controlScores[standardid][controltitle] += maturityScore;
					// console.log(`[calculateAverageScoresByControlTitles] [${standardid}] Added score ${maturityScore} to ${controltitle} (SafeguardID: ${safeguardId})`);
					
					// Extra debug for Cyber risk program
					if (controltitle === 'Cyber risk program') {
						// console.log(`[DEBUG] Cyber risk program match: SafeguardID=${safeguardId}, Score=${maturityScore}`);
					}
				} else if (controltitle === 'Cyber risk program' && maturityScore > 0) {
					// Log when a project item with a score doesn't match Cyber risk program
					// console.log(`[DEBUG] Cyber risk program NO match: SafeguardID=${safeguardId}, Score=${maturityScore} (not in mappings)`);
				}
			});
		});
	});

	// Calculate average scores for each controltitle under each standardid, only FCA-RM-2024
	const averageScores = {};
	Object.keys(controlScores).forEach(standardid => {
		if (standardid !== 'FCA-RM-2024') return;
		// console.log(`[calculateAverageScoresByControlTitles] Calculating final averages for standard: ${standardid}`);
		averageScores[standardid] = {}; // Initialize average score object for each standardid

		Object.keys(controlScores[standardid]).forEach(controltitle => {
			const count = controlCounts[standardid][controltitle];
			const totalScore = controlScores[standardid][controltitle];

			if (count > 0) {
				// Calculate the average score for the controltitle
				averageScores[standardid][controltitle] = (totalScore / count).toFixed(2);
				// console.log(`[calculateAverageScoresByControlTitles] [${standardid}] ${controltitle}: Average = ${averageScores[standardid][controltitle]} (Total: ${totalScore}, Count: ${count})`);
			} else {
				// If no mappings, set the score to 0
				averageScores[standardid][controltitle] = (0).toFixed(2);
				// console.log(`[calculateAverageScoresByControlTitles] [${standardid}] ${controltitle}: No valid mappings, setting score to 0.00`);
			}
		});
	});

	// Debug: Show missing mappings for FCA-RM-2024
	if (groupedControlTitles['FCA-RM-2024']) {
		// console.log('[DEBUG] === FCA-RM-2024 MAPPING ANALYSIS ===');
		Object.keys(groupedControlTitles['FCA-RM-2024']).forEach(controltitle => {
			const expectedMappings = groupedControlTitles['FCA-RM-2024'][controltitle].crfmappings;
			const foundMappings = [];
			const missingMappings = [];
			
			expectedMappings.forEach(mapping => {
				if (allSafeguardIdsWithScores.includes(mapping)) {
					foundMappings.push(mapping);
				} else {
					missingMappings.push(mapping);
				}
			});
			
			// console.log(`[DEBUG] ${controltitle}:`);
			// console.log(`  Expected: ${expectedMappings.length} mappings`);
			// console.log(`  Found: ${foundMappings.length} mappings:`, foundMappings);
			// console.log(`  Missing: ${missingMappings.length} mappings:`, missingMappings);
		});
	}

		// console.log('[calculateAverageScoresByControlTitles] Final FCA-RM-2024 average scores:', averageScores['FCA-RM-2024']);
		return averageScores;
	}

	// Export for use in other modules
	if (typeof module !== 'undefined' && module.exports) {
		module.exports = { calculateAverageScoresByControlTitles };
	} else if (typeof window !== 'undefined') {
		window.calculateAverageScoresByControlTitles = calculateAverageScoresByControlTitles;
	}

})(jQuery);
